import { HugeiconsIcon } from "@hugeicons/react";
import {
    Cancel01Icon,
    IdeaIcon,
} from "@hugeicons/core-free-icons";

import {
    Box,
    Button,
    Checkbox,
    Dialog,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useCreateTutorialSuggestionMutation } from "@/api/tutorialsApi";
import useSnackbar from "@/hooks/useSnackbar";

interface SuggestionDialogProps {
    open: boolean;
    onClose: () => void;
}

interface SuggestionFormValues {
    title: string;
    message: string;
    notifyByEmail: boolean;
}

const initialValues: SuggestionFormValues = {
    title: "",
    message: "",
    notifyByEmail: false,
};

const suggestionSchema = Yup.object({
    title: Yup.string()
        .trim()
        .required("El título es requerido")
        .max(100, "El título no puede superar los 100 caracteres"),

    message: Yup.string()
        .trim()
        .required("Cuéntanos qué te gustaría aprender")
        .max(250, "La descripción no puede superar los 250 caracteres"),

    notifyByEmail: Yup.boolean(),
});

const SuggestionDialog = ({ open, onClose }: SuggestionDialogProps) => {
    const [openSnackbar] = useSnackbar();
    const [createSuggestion] = useCreateTutorialSuggestionMutation();

    const handleClose = (resetForm: () => void, isSubmitting: boolean) => {
        if (isSubmitting) return;
        resetForm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <Formik
                initialValues={initialValues}
                validationSchema={suggestionSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                    try {
                        await createSuggestion({
                            title: values.title.trim(),
                            message: values.message.trim(),
                            notifyByEmail: values.notifyByEmail,
                        }).unwrap();
                        openSnackbar("Sugerencia enviada exitosamente", "success");
                        resetForm();
                        onClose();
                    } catch (error) {
                        console.error("Error creating tutorial suggestion:", error);
                        openSnackbar("Error al enviar sugerencia", "error");
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting, resetForm }) => {
                    return (
                        <Form>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                p={3}
                                spacing={2}
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <Box
                                        sx={{
                                            bgcolor: "primary.light",
                                            color: "primary.main",
                                            borderRadius: 3,
                                            padding: 1,
                                            width: 36,
                                            height: 36,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <HugeiconsIcon icon={IdeaIcon} />
                                    </Box>

                                    <Stack>
                                        <Typography variant="h3">
                                            Sugerir tema
                                        </Typography>

                                        <Typography variant="caption" color="text.secondary">
                                            Ayúdanos a construir la biblioteca de
                                            aprendizaje perfecta para tu negocio.
                                        </Typography>
                                    </Stack>
                                </Stack>

                                <IconButton
                                    onClick={() =>
                                        handleClose(resetForm, isSubmitting)
                                    }
                                    component={Paper}
                                    sx={{ backgroundColor: "#ffffff" }}
                                    elevation={2}
                                    color="primary"
                                    disabled={isSubmitting}
                                >
                                    <HugeiconsIcon icon={Cancel01Icon} />
                                </IconButton>
                            </Stack>

                            <Divider />

                            <Stack spacing={2} padding={3}>
                                <TextField
                                    fullWidth
                                    label="Título"
                                    placeholder="Ej. Configurar tiempos de preparación"
                                    name="title"
                                    value={values.title}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                        touched.title && Boolean(errors.title)
                                    }
                                    helperText={
                                        touched.title ? errors.title : undefined
                                    }
                                    inputProps={{
                                        maxLength: 100,
                                    }}
                                />
                                <Stack spacing={1}>

                                    <Typography fontWeight={600}>
                                        ¿Qué te gustaría aprender?
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        name="message"
                                        placeholder="Ej. Me gustaría saber cómo configurar tiempos de preparación automáticos por estación..."
                                        value={values.message}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.message && Boolean(errors.message)
                                        }
                                        helperText={
                                            touched.message
                                                ? errors.message
                                                : `${values.message.length} / 250`
                                        }
                                        inputProps={{
                                            maxLength: 250,
                                        }}
                                    />
                                </Stack>
                            </Stack>
                            <Divider />
                            <Stack
                                padding={3}
                                spacing={2}
                                direction="row"
                                justifyContent="space-between"
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="notifyByEmail"
                                            checked={values.notifyByEmail}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                        />
                                    }
                                    label="Enviarme correo cuando esté listo"
                                />

                                <Stack direction="row" spacing={2}>
                                    <Button
                                        onClick={() =>
                                            handleClose(resetForm, isSubmitting)
                                        }
                                        size="small"
                                        variant="text"
                                        color="error"
                                        disabled={isSubmitting}
                                    >
                                        Cancelar
                                    </Button>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="small"
                                        disabled={isSubmitting || !values.title || !values.message}
                                    >
                                        {isSubmitting ? "Enviando..." : "Enviar"}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default SuggestionDialog;
